import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  ShoppingCart, 
  DollarSign, 
  TrendingUp, 
  Users,
  CreditCard,
  QrCode,
  Smartphone,
  Plus
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { SupabaseService } from '../lib/supabase'
import toast from 'react-hot-toast'

const MerchantDashboard = () => {
  const { profile } = useAuth()
  const [stats, setStats] = useState({
    totalSales: 0,
    todaySales: 0,
    totalCustomers: 0,
    conversionRate: 0
  })
  const [recentPayments, setRecentPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  useEffect(() => {
    loadMerchantData()
  }, [])

  const loadMerchantData = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      
      // Charger les vraies données depuis Supabase
      const payments = await SupabaseService.getPayments(user.id, 50)
      const completedPayments = payments.filter(p => p.status === 'completed')
      
      // Calculer les statistiques réelles
      const totalSales = completedPayments.reduce((sum, p) => sum + p.amount, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const todayPayments = completedPayments.filter(p => new Date(p.created_at) >= today)
      const todaySales = todayPayments.reduce((sum, p) => sum + p.amount, 0)
      const uniqueCustomers = new Set(completedPayments.map(p => p.phone)).size
      const conversionRate = payments.length > 0 ? (completedPayments.length / payments.length) * 100 : 0
      
      setStats({
        totalSales,
        todaySales,
        totalCustomers: uniqueCustomers,
        conversionRate
      })

      // Récupérer les 5 derniers paiements
      setRecentPayments(payments.slice(0, 5).map(p => ({
        id: p.id,
        amount: p.amount,
        customer: p.phone,
        provider: p.provider,
        status: p.status,
        created_at: p.created_at
      })))
      
    } catch (error) {
      console.error('Erreur chargement données commerçant:', error)
      toast.error('Erreur lors du chargement')
      
      // Initialiser avec des valeurs vides en cas d'erreur
      setStats({
        totalSales: 0,
        todaySales: 0,
        totalCustomers: 0,
        conversionRate: 0
      })
      setRecentPayments([])
    } finally {
      setLoading(false)
    }
  }

  const salesData = [
    { day: 'Lun', sales: 45000 },
    { day: 'Mar', sales: 52000 },
    { day: 'Mer', sales: 38000 },
    { day: 'Jeu', sales: 61000 },
    { day: 'Ven', sales: 75000 },
    { day: 'Sam', sales: 89000 },
    { day: 'Dim', sales: 67000 }
  ]

  const paymentMethods = [
    { name: 'Orange Money', count: 45, color: '#FF6B35' },
    { name: 'Wave', count: 32, color: '#4ECDC4' },
    { name: 'MTN MoMo', count: 18, color: '#FFD93D' }
  ]

  const merchantCards = [
    {
      title: 'Ventes Totales',
      value: `${(stats.totalSales / 1000).toFixed(0)}k XOF`,
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      title: 'Ventes Aujourd\'hui',
      value: `${(stats.todaySales / 1000).toFixed(0)}k XOF`,
      change: '+8.2%',
      trend: 'up',
      icon: TrendingUp,
      color: 'text-blue-600'
    },
    {
      title: 'Clients',
      value: stats.totalCustomers.toString(),
      change: '+15.3%',
      trend: 'up',
      icon: Users,
      color: 'text-purple-600'
    },
    {
      title: 'Taux de Conversion',
      value: `${stats.conversionRate}%`,
      change: '+2.1%',
      trend: 'up',
      icon: ShoppingCart,
      color: 'text-orange-600'
    }
  ]

  const quickActions = [
    {
      title: 'Nouveau Paiement',
      description: 'Créer un paiement manuel',
      icon: Plus,
      color: 'bg-blue-600 hover:bg-blue-700',
      action: () => setShowPaymentModal(true)
    },
    {
      title: 'QR Code',
      description: 'Générer un QR de paiement',
      icon: QrCode,
      color: 'bg-purple-600 hover:bg-purple-700',
      action: () => toast.info('Fonctionnalité QR Code')
    },
    {
      title: 'Lien de Paiement',
      description: 'Créer un lien partageable',
      icon: Smartphone,
      color: 'bg-green-600 hover:bg-green-700',
      action: () => toast.info('Fonctionnalité Lien de Paiement')
    }
  ]

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Espace Commerçant
        </h1>
        <p className="text-gray-600">
          Bienvenue {profile?.name}, gérez vos paiements et suivez vos ventes
        </p>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {merchantCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{card.value}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm ml-1 text-green-600">{card.change}</span>
                  <span className="text-xs text-gray-500 ml-1">vs hier</span>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50">
                <card.icon className={`w-6 h-6 ${card.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Actions Rapides */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
      >
        {quickActions.map((action, index) => (
          <motion.button
            key={action.title}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={action.action}
            className={`${action.color} text-white p-6 rounded-lg transition-all text-left`}
          >
            <action.icon className="w-8 h-8 mb-3" />
            <h3 className="font-semibold mb-1">{action.title}</h3>
            <p className="text-sm opacity-90">{action.description}</p>
          </motion.button>
        ))}
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Graphique des ventes */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Ventes de la Semaine</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip 
                  formatter={(value) => [`${value} XOF`, 'Ventes']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Bar dataKey="sales" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Méthodes de paiement */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Méthodes de Paiement Populaires</h2>
          <div className="space-y-4">
            {paymentMethods.map((method, index) => (
              <div key={method.name} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-3" 
                    style={{ backgroundColor: method.color }}
                  />
                  <span className="font-medium">{method.name}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-3">{method.count} paiements</span>
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full" 
                      style={{ 
                        backgroundColor: method.color,
                        width: `${(method.count / 95) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Paiements récents */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm border p-6"
      >
        <h2 className="text-lg font-semibold mb-4">Paiements Récents</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">ID</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Montant</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Client</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Provider</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Statut</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentPayments.map((payment: any, index) => (
                <motion.tr
                  key={payment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-3 px-4 font-mono text-sm">{payment.id}</td>
                  <td className="py-3 px-4 font-semibold">{payment.amount.toLocaleString()} XOF</td>
                  <td className="py-3 px-4 font-mono text-sm">{payment.customer}</td>
                  <td className="py-3 px-4">{payment.provider}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                      Complété
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {new Date(payment.created_at).toLocaleString()}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Modal de paiement */}
      {showPaymentModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            <h2 className="text-xl font-semibold mb-4">Nouveau Paiement</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant (XOF)
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+221771234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Provider
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="orange_money">Orange Money</option>
                  <option value="wave">Wave</option>
                  <option value="mtn_momo">MTN MoMo</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  toast.success('Paiement créé!')
                  setShowPaymentModal(false)
                }}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default MerchantDashboard